"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  ArrowLeft,
  ArrowRight,
  ChartColumn,
  Check,
  CheckCircle2,
  CircleHelp,
  Code2,
  FlaskConical,
  LayoutDashboard,
  ListChecks,
  Loader2,
  Lock,
  LogIn,
  Megaphone,
  MessageCircleQuestion,
  ScrollText,
  Send,
  ShieldAlert,
  Snowflake,
  Timer,
  Trophy,
  Building2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { smoothScrollBehavior } from "@/lib/scroll-behavior";
import { formatNumber } from "@/lib/format-datetime";
import { Container } from "@/components/ui/container";
import { HeadTabs, type HeadTabItem } from "@/components/ui/head-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog } from "@/components/ui/dialog";
import { ContestProfileForm } from "@/features/profile/components/ContestProfileForm";
import { useAuth } from "@/components/providers/auth-provider";
import { VerdictChip } from "@/features/judge/components/workspace/SubmissionResult";
import {
  useAnnouncementInbox,
  useClarificationInbox,
  useContestAnnouncements,
  useContestStandings,
  useMyContestSubmissions,
  usePublicContest,
  useRegisterForContest,
} from "../hooks";
import { useNow } from "../lib/use-now";
import { useContestPageView } from "../lib/use-contest-page-view";
import { contestErrorKey, formatClockTime, formatDateTime, remainingUntil } from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import { canReadRehearsals, canRehearseNow } from "../lib/rehearsal";
import {
  CONTEST_PASSPHRASE_MAX,
  type ContestProblemIndexItem,
  type ContestStandingsResponse,
  type PublicContestDetail,
} from "../types";
import { ContestCalendarLinks } from "./ContestCalendarLinks";
import { ContestCertificateDoor } from "./ContestCertificateDoor";
import { ContestClarificationsPanel } from "./ContestClarificationsPanel";
import { ContestFirstTimerFaq } from "./ContestFirstTimerFaq";
import { ContestHead } from "./ContestHead";
import { ContestOverview } from "./ContestOverview";
import { ContestStickyBar } from "./ContestStickyBar";
import { ContestAnnouncementAlert } from "./ContestAnnouncementAlert";
import { ContestClarificationAlert } from "./ContestClarificationAlert";
import { useTitleCount } from "../lib/use-title-count";
import { ContestKioskView } from "./ContestKioskView";
import { ContestProblemList } from "./ContestProblemList";
import { ProblemSetPdfBar, ProblemSetPdfDoor, StaffProblemSetPdf } from "./ContestProblemSetPdf";
import { ContestRehearsalPanel } from "./ContestRehearsalPanel";
import { ContestSubmissionsPanel } from "./ContestSubmissionsPanel";
import { ContestStandingsPanel } from "./ContestStandingsPanel";
import { InstitutionsPanel } from "./season/InstitutionsPanel";
import { SeasonPanel } from "./season/SeasonPanel";
import { WarmUpDoorNote } from "./WarmUpDoorNote";
import { LaterRegistrationQuestions, RegistrationQuestionsDialog } from "./RegistrationQuestionsDialog";
import { TalentPassCard, TalentPassDoorTick, talentPassMayShow } from "./TalentPass";
import type { RegistrationAnswerInput } from "../registration";
import { DivisionDoorNote } from "./DivisionDoorNote";
import { VirtualBar, VirtualBoardSwitch, VirtualDoor, VirtualResultCard, virtualOf } from "./virtual/VirtualRound";

/**
 * `EX-EDR05`: the "How to submit" guide, its own chunk, fetched at the first
 * open. Before the start the page offers it whatever the owner's switch says.
 */
const ContestSubmitGuide = dynamic(() => import("./ContestSubmitGuide").then((m) => m.ContestSubmitGuide), {
  ssr: false,
});

type Tab =
  | "overview"
  | "problems"
  | "standings"
  | "submissions"
  | "rehearsal"
  | "announcements"
  | "clarifications"
  // `EX-INS01`: a round's institutions after the end, and the seasons it is in.
  | "institutions"
  | "season";

/**
 * `EX-PUB14`: every tab is addressable, because the workspace bar links at one.
 *
 * A contestant reading the scoreboard from problem B arrives here on
 * `?tab=standings` and lands on the scoreboard, not on the problem list with a
 * click still to make. The value is read from `window.location` in an effect
 * rather than through `useSearchParams`, which would put this statically
 * rendered page behind a Suspense boundary for a value only the client uses.
 */
const TAB_VALUES: readonly Tab[] = [
  "overview",
  "problems",
  "standings",
  "submissions",
  "rehearsal",
  "announcements",
  "clarifications",
  "institutions",
  "season",
];

function isTab(value: string | null): value is Tab {
  return value !== null && (TAB_VALUES as readonly string[]).includes(value);
}

/** The door's full-width actions, on its dark card (`EX-PUB22`). */
/**
 * `EX-SPN01`: the door's primary actions are `Button`'s `accent`, the brand
 * orange until the contest head sets the organizer's colour through the
 * quiz's guard (`contestAccentStyle`).
 */
const DOOR_BUTTON = "h-[46px] w-full rounded-[10px] text-[15px]";

/** `EX-CPG02`: each tab's icon on the head's tabs (the canvas's TabBar board). */
const TAB_ICON: Readonly<Record<Tab, ReactNode>> = {
  overview: <LayoutDashboard className="h-4 w-4" />,
  problems: <ListChecks className="h-4 w-4" />,
  standings: <ChartColumn className="h-4 w-4" />,
  submissions: <Send className="h-4 w-4" />,
  rehearsal: <FlaskConical className="h-4 w-4" />,
  announcements: <Megaphone className="h-4 w-4" />,
  clarifications: <MessageCircleQuestion className="h-4 w-4" />,
  institutions: <Building2 className="h-4 w-4" />,
  season: <Trophy className="h-4 w-4" />,
};

/**
 * `EX-ARC01`, `docs/29` §6.4: the round's problems are in the public practice
 * archive, and each row of the list below links its own practice page.
 *
 * Rendered off `practiceSlug` rather than off the round's `archivedAt`, and
 * that is the point: a round can be archived and still have published nothing,
 * because every one of its problems was refused (attached to an exam, curated
 * in the bank, reserved by its author, or failing its reference solution). A
 * notice keyed on the stamp would claim an archive that is not there.
 *
 * `EX-PUB20`: the converse holds too. Problems can be public without the round
 * ever being archived (the Warm-up Round's were, on 2026-09-23), and only the
 * archive act gives a problem page its round credit (`resolveArchivedProblemCredit`
 * reads `archivedAt`). So the sentence that promises the credit renders only
 * once the round is archived; before that the notice says what is true, that
 * the problems are open for practice.
 *
 * `EX-PUB22` moved the practice links from this notice onto the rows, one per
 * problem, where a reader choosing a problem already is.
 */
function ArchiveNotice({
  problems,
  roundArchived,
}: {
  problems: ContestProblemIndexItem[];
  roundArchived: boolean;
}) {
  const t = useTranslations("contest.page");
  if (!problems.some((problem) => problem.practiceSlug)) return null;

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border px-4 py-3.5 text-sm leading-relaxed sm:px-[18px]",
        roundArchived ? "border-emerald-200 bg-emerald-50" : "border-neutral-200 bg-white",
      )}
    >
      {roundArchived ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <Code2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
      )}
      <div>
        <p className={cn("font-semibold", roundArchived ? "text-emerald-900" : "text-neutral-900")}>
          {t(roundArchived ? "archiveTitle" : "archiveOpenTitle")}
        </p>
        <p className={roundArchived ? "text-emerald-800" : "text-neutral-600"}>
          {t(roundArchived ? "archiveBody" : "archiveOpenBody")}
        </p>
      </div>
    </div>
  );
}

/**
 * The registration door - `docs/29` §5, and every branch of it is the server's.
 *
 * `canRegister` already folds "authenticated, not a participant, the window has
 * not closed, and this is not the one door a contestant cannot open themselves".
 * The statuses beside it are `EX-PUB06`'s: a pending, blocked or disqualified
 * contestant still loads this page precisely so it can tell them why they
 * cannot take part.
 *
 * `EX-PUB22` drew it on the head's dark card, told a stranger who may register
 * before asking them to sign in, and gave a registered contestant the way into
 * a running round. A finished contest's door is {@link EndedDoor}.
 */
function RegistrationDoor({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const tErrors = useTranslations("contest.errors");
  const register = useRegisterForContest(contest.slug);

  const [passphrase, setPassphrase] = useState("");
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `EX-IDN06` (ADR-0088 decision 1): the box that asks for what the profile misses.
  const [profileOpen, setProfileOpen] = useState(false);
  // `EX-REG01`: the organizer's questions, the door's second step.
  const questions = contest.registrationQuestions ?? [];
  const [questionsOpen, setQuestionsOpen] = useState(false);
  // `EX-TAL01`: the Talent Pass tick, given by the card once the registration lands.
  const talentShown = talentPassMayShow(contest);
  const [talentTick, setTalentTick] = useState(false);
  const { user } = useAuth();
  // `RV-20`: signing in from the door comes back to this contest page.
  const signInHref = useSignInHref(locale);

  const { viewer } = contest;
  const tDivision = useTranslations("contest.division");
  // `EX-DIV03`: the server's word on this reader against the division.
  const unofficialDoor = viewer.division?.standing === "unofficial";
  const unofficialEntry = viewer.division?.registeredAs === "unofficial" && unofficialDoor;
  // `EX-DAY04`: a warm-up's door asks nothing the main contest's did not.
  const profileIncomplete =
    user?.accountKind !== "contest_temp" &&
    contest.registration !== "linked" &&
    user?.contestProfile?.complete === false;

  const registerNow = async (answers?: RegistrationAnswerInput[]) => {
    setError(null);
    try {
      await register.mutateAsync({
        ...(passphrase ? { passphrase } : {}),
        acceptedRules,
        ...(answers ? { answers } : {}),
      });
      setPassphrase("");
      setQuestionsOpen(false);
    } catch (err) {
      // The API is the judge: a profile it finds incomplete opens the box even if the page thought otherwise.
      if (err instanceof ApiError && err.code === "PROFILE_INCOMPLETE") {
        setProfileOpen(true);
        return;
      }
      const key = contestErrorKey(err);
      setError(key ? tErrors(key) : err instanceof ApiError ? err.message : t("registerFailed"));
    }
  };

  const submit = async () => {
    // Ask in place, then register in the same action; never a trip to settings.
    if (profileIncomplete) {
      setProfileOpen(true);
      return;
    }
    if (questions.length > 0 && contest.registration !== "linked") {
      setQuestionsOpen(true);
      return;
    }
    await registerNow();
  };

  if (!viewer.isAuthenticated) {
    // A door the reader cannot open themselves says so first: an invited or an
    // issued roster is the organizer's, and a sign-in would only find that out.
    if (contest.registration === "invite" || contest.registration === "issued" || contest.registration === "linked") {
      return (
        <div className="space-y-3 text-sm">
          <p className="leading-relaxed text-neutral-300">
            {contest.registration === "invite"
              ? tErrors("inviteOnly")
              : contest.registration === "linked"
                ? t("warmUp.signedOut")
                : t("issuedOnly")}
          </p>
          <Link
            href={signInHref}
            className="font-semibold text-accent-peach transition-colors duration-200 hover:text-white"
          >
            {t("signInIfAdded")}
          </Link>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-neutral-300">
          {contest.registration === "passphrase"
            ? t("doorPassphrase")
            : contest.registration === "course"
              ? t("courseNotice")
              : t("doorOpen")}
        </p>
        <Button asChild variant="accent" className={DOOR_BUTTON}>
          <Link href={signInHref}>
            <LogIn className="h-4 w-4" aria-hidden />
            {t("signInToRegister")}
          </Link>
        </Button>
      </div>
    );
  }

  if (viewer.participantStatus === "blocked" || viewer.participantStatus === "disqualified") {
    return (
      <div className="flex items-start gap-2 rounded-[10px] border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>
          {viewer.participantStatus === "blocked"
            ? tErrors("participantBlocked")
            : tErrors("participantDisqualified")}
        </p>
      </div>
    );
  }

  // `EX-STF01`, `docs/29` §5.1: staff never compete, and the page says which
  // list names them rather than showing a register button the server refuses.
  if (viewer.staffRole) {
    return (
      <p className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-neutral-300">
        {t("staffNotice", { role: t(`staffRole_${viewer.staffRole}`) })}
      </p>
    );
  }

  // `EX-OFF01`, ADR-0099: an operator's account does not compete in a rated
  // round, and the page says so rather than showing a button the server refuses.
  if (viewer.operatorAccount) {
    return (
      <p
        className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-neutral-300"
        data-testid="operator-notice"
      >
        {t("operatorNotice")}
      </p>
    );
  }

  if (viewer.participantStatus === "pending") {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-[10px] border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
          <Timer className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>{tErrors("registrationPending")}</p>
        </div>
        <LaterRegistrationQuestions contest={contest} />
        {talentShown ? <TalentPassCard slug={contest.slug} autoGive={talentTick} /> : null}
      </div>
    );
  }

  if (viewer.isCompeting && !viewer.needsRulesAcceptance) {
    // While the round runs, the way in is its first problem: the workspace bar
    // there reaches every other letter.
    const firstLetter = contest.isOpen ? contest.problems?.[0]?.letter : undefined;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-[10px] border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <p>{t("registered")}</p>
            {/* `EX-DIV03`: an unofficial entry says so where the entry is confirmed. */}
            {unofficialEntry ? (
              <span
                data-testid="registered-unofficial"
                className="rounded-full border border-white/20 px-2.5 py-0.5 text-xs font-semibold text-neutral-200"
              >
                {tDivision("unofficial")}
              </span>
            ) : null}
            {viewer.team ? (
              <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-semibold text-white">
                {viewer.team.name}
              </span>
            ) : contest.participation === "team" ? (
              <span className="text-xs font-medium text-amber-300">{t("noTeamYet")}</span>
            ) : null}
            {/*
              `EX-ONS01`: where they sit, when the organizer has seated them. Their
              own entry's seat and nobody else's (`docs/29` §6.2); absent, nothing
              is drawn, so an online round's card is the card it always was.
            */}
            {viewer.seat ? (
              <span
                data-testid="viewer-seat"
                className="rounded-full bg-white/10 px-2.5 py-0.5 font-mono text-xs font-semibold text-emerald-100"
              >
                {viewer.seat.room ? t("yourSeat", { room: viewer.seat.room, label: viewer.seat.label }) : viewer.seat.label}
              </span>
            ) : null}
          </div>
        </div>
        {/* `EX-REG01`: a question the organizer added after they registered. */}
        <LaterRegistrationQuestions contest={contest} />
        {/* `EX-TAL01`: whether this round's sponsors may see their profile. */}
        {talentShown ? <TalentPassCard slug={contest.slug} autoGive={talentTick} /> : null}
        {firstLetter ? (
          <Button asChild variant="accent" className={DOOR_BUTTON}>
            <Link href={`/${locale}/contests/${contest.slug}/problems/${firstLetter}`}>
              {t(contest.official ? "enterRound" : "enterContest")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        ) : null}
      </div>
    );
  }

  // `EX-DIV03`, Wave 29 decision 3: a closed division's box above says why;
  // a second "registration is closed" here would read as a different reason.
  if (viewer.division?.standing === "closed" && !viewer.isRegistered) return null;

  if (!viewer.canRegister && !viewer.needsRulesAcceptance) {
    return (
      <p className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-neutral-300">
        {contest.registration === "invite"
          ? tErrors("inviteOnly")
          : contest.registration === "linked"
            ? t("warmUp.entrantsOnly")
            : t("registrationClosed")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        title={t("profileBox.title")}
        description={t("profileBox.lead")}
        closeLabel={t("profileBox.close")}
      >
        <ContestProfileForm
          locale={locale}
          submitLabel={t("profileBox.saveAndRegister")}
          onSaved={async () => {
            setProfileOpen(false);
            if (questions.length > 0 && contest.registration !== "linked") setQuestionsOpen(true);
            else await registerNow();
          }}
        />
      </Dialog>
      <RegistrationQuestionsDialog
        open={questionsOpen}
        onClose={() => setQuestionsOpen(false)}
        slug={contest.slug}
        title={contest.title_en}
        questions={questions}
        pending={register.isPending}
        error={questionsOpen ? error : null}
        onSubmit={(answers) => void registerNow(answers)}
      />
      {contest.registration === "passphrase" ? (
        <Input
          type="password"
          value={passphrase}
          onChange={(event) => setPassphrase(event.target.value.slice(0, CONTEST_PASSPHRASE_MAX))}
          placeholder={t("passphrasePlaceholder")}
          className="h-11 rounded-[10px] border-white/15 bg-white/5 text-white placeholder:text-neutral-500 focus-visible:ring-offset-0"
        />
      ) : null}
      <Button
        type="button"
        // `EX-DIV03`: outside the division the door still opens, as the second action.
        // `EX-SPN01`: `accent` is the brand orange until the organizer's colour is set on the head.
        variant={unofficialDoor ? "outlineDark" : "accent"}
        onClick={() => void submit()}
        disabled={
          register.isPending ||
          !acceptedRules ||
          (contest.registration === "passphrase" && !passphrase.trim())
        }
        className={DOOR_BUTTON}
      >
        {register.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        {contest.registration === "linked"
          ? t("warmUp.enterButton")
          : unofficialDoor
            ? tDivision("registerUnofficially")
            : t("register")}
      </Button>
      <label className="flex items-start gap-2.5 text-[13px] leading-relaxed text-neutral-400">
        <input
          type="checkbox"
          checked={acceptedRules}
          onChange={(event) => setAcceptedRules(event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 bg-transparent text-primary focus:ring-primary"
        />
        <span>
          {t.rich("acceptRules", {
            link: (chunks) => (
              <Link
                href={`/${locale}/contests/${contest.slug}/rules`}
                className="font-medium text-accent-peach underline underline-offset-2"
              >
                {chunks}
              </Link>
            ),
          })}
        </span>
      </label>
      {talentShown && contest.registration !== "linked" ? (
        <TalentPassDoorTick slug={contest.slug} checked={talentTick} onChange={setTalentTick} />
      ) : null}
      {/*
        `EX-INT04`: a contestant sees the profile **before** registering, not
        after. One sentence beside the checkbox they are about to tick, and the
        rules page it already links to carries the full list.

        It describes what is recorded rather than labelling the round, which is
        the same decision the rules page and the workspace notice made.
      */}
      {contest.integrity && contest.integrity.profile !== "honour" ? (
        <p className="text-xs leading-relaxed text-neutral-400">
          {/* `EX-WSP01`: the sentence changes when the editor is kept, because
              "nothing you type is read" would then be false. */}
          {t(
            // `EX-EDO01`: the Editor only level says what it adds.
            contest.integrity.profile === "editor_only"
              ? "integrityNoticeEditorOnly"
              : contest.integrity.workspaceSnapshots
                ? "integrityNoticeSnapshots"
                : "integrityNotice",
          )}
        </p>
      ) : null}
      {contest.requiresApproval ? (
        <p className="text-xs leading-relaxed text-neutral-400">{t("approvalNotice")}</p>
      ) : null}
      {contest.registration === "course" ? (
        <p className="text-xs leading-relaxed text-neutral-400">{t("courseNotice")}</p>
      ) : null}
      {error && !questionsOpen ? (
        <p className="rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * The countdown on the door (`EX-PUB22`): to the start while the contest is
 * scheduled, to the end while it runs, with what the freeze will do or is
 * doing. Days only when there are days left. Nothing at all before the clock
 * has ticked once, so a server render never prints a time already wrong.
 */
function DoorCountdown({ contest, now, locale }: { contest: PublicContestDetail; now: number | null; locale: string }) {
  const t = useTranslations("contest.page");
  const target = contest.isOpen ? contest.endAt : contest.startAt;
  const remaining = now === null ? null : remainingUntil(target, now);
  const freezeMinutes =
    contest.freezeAt && contest.endAt
      ? Math.round((new Date(contest.endAt).getTime() - new Date(contest.freezeAt).getTime()) / 60_000)
      : null;

  const freezeLine =
    contest.phase === "frozen"
      ? t("freezeNow")
      : contest.phase === "running" && freezeMinutes !== null && freezeMinutes > 0
        ? t("freezeAhead", { minutes: formatNumber(freezeMinutes, locale) })
        : null;

  if (!remaining || remaining.total <= 0) {
    return freezeLine ? <p className="mb-5 text-sm leading-relaxed text-neutral-300">{freezeLine}</p> : null;
  }

  const label = contest.isOpen ? t("endsIn") : t("startsIn");
  const units = [
    ...(remaining.days > 0 ? [{ key: "unitDays", value: remaining.days }] : []),
    { key: "unitHours", value: remaining.hours },
    { key: "unitMinutes", value: remaining.minutes },
    { key: "unitSeconds", value: remaining.seconds },
  ];

  return (
    <div className="mb-5">
      <p className="text-[11.5px] font-bold uppercase tracking-[0.16em] text-neutral-400">{label}</p>
      <div role="timer" aria-live="off" aria-label={label} className="mt-3 grid auto-cols-fr grid-flow-col gap-2">
        {units.map((unit) => (
          <span key={unit.key} className="flex flex-col items-center gap-1.5">
            <span className="flex h-14 w-full items-center justify-center rounded-[10px] border border-white/10 bg-white/5 font-mono text-[26px] font-bold tabular-nums text-white sm:h-[62px] sm:text-3xl">
              {String(unit.value).padStart(2, "0")}
            </span>
            <span className="text-[11px] text-neutral-400">{t(unit.key)}</span>
          </span>
        ))}
      </div>
      {freezeLine ? <p className="mt-4 text-sm leading-relaxed text-neutral-300">{freezeLine}</p> : null}
    </div>
  );
}

/**
 * `EX-PUB22`: who won a finished contest, read off the first page of its
 * standings. Rank 1 is the fold's, not this function's. Nobody is named while
 * the table is still frozen, when the top of it solved nothing, or when first
 * place is shared (then only the tie is said).
 */
export function winnerOf(
  standings: Pick<ContestStandingsResponse, "rows" | "frozen"> | undefined,
):
  | { kind: "one"; name: string; solved: number; penaltyMinutes: number; score: number }
  | { kind: "tie" }
  | null {
  if (!standings || standings.frozen) return null;
  const top = standings.rows.filter((row) => row.rank === 1);
  if (top.length === 0 || top.every((row) => row.solved === 0 && row.score === 0)) return null;
  if (top.length > 1) return { kind: "tie" };
  const row = top[0]!;
  if (!row.name) return null;
  return { kind: "one", name: row.name, solved: row.solved, penaltyMinutes: row.penaltyMinutes, score: row.score };
}

/**
 * `EX-PUB20`: a finished contest has no door to open, for anybody, and says so
 * before it asks who the reader is. It used to ask a stranger to sign in and
 * register for a round that had ended days before. `ended` with a freeze is
 * the state a moderator has not yet unfrozen (`docs/29` §6): the board on
 * this page is still the frozen one, and the page must not read as final.
 *
 * `EX-PUB22`: a final board names its winner, from the standings read the
 * problem list already makes (same key, one request), and the practice copies
 * are one button away.
 */
function EndedDoor({
  contest,
  locale,
  onShowStandings,
  onShowProblems,
}: {
  contest: PublicContestDetail;
  locale: string;
  onShowStandings: () => void;
  onShowProblems: () => void;
}) {
  const t = useTranslations("contest.page");
  const awaitingFinal = contest.phase === "ended" && contest.freezeAt !== null;
  const hasPractice = (contest.problems ?? []).some((problem) => problem.practiceSlug);
  const standings = useContestStandings(
    contest.slug,
    { view: "auto", page: 1 },
    !awaitingFinal && (contest.problems?.length ?? 0) > 0,
  );
  const winner = awaitingFinal ? null : winnerOf(standings.data);
  const bold = (chunks: ReactNode) => <b className="font-semibold text-white">{chunks}</b>;

  return (
    <div>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]",
            awaitingFinal ? "bg-sky-400/15 text-sky-300" : "bg-emerald-400/15 text-emerald-300",
          )}
        >
          {awaitingFinal ? <Snowflake className="h-5 w-5" aria-hidden /> : <Trophy className="h-5 w-5" aria-hidden />}
        </span>
        <div className="min-w-0">
          <p className="text-lg font-bold tracking-tight text-white">
            {t(contest.official ? "endedRound" : "endedContest")}
          </p>
          {awaitingFinal ? (
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-300">{t("finalNotPublished")}</p>
          ) : null}
          {winner ? (
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-300">
              {winner.kind === "tie"
                ? t("winnerTie")
                : contest.scoringFormat === "icpc"
                  ? t.rich("winnerIcpc", {
                      b: bold,
                      name: winner.name,
                      solved: formatNumber(winner.solved, locale),
                      penalty: formatNumber(winner.penaltyMinutes, locale),
                    })
                  : t.rich("winnerScore", {
                      b: bold,
                      name: winner.name,
                      score: formatNumber(winner.score, locale),
                    })}
            </p>
          ) : null}
        </div>
      </div>
      {/* `EX-VRT01`: an archived round, taken on the reader's own clock. */}
      <VirtualDoor contest={contest} locale={locale} onShowProblems={onShowProblems} />
      <div className="mt-5 grid gap-2.5">
        <Button type="button" variant="inverse" onClick={onShowStandings} className={DOOR_BUTTON}>
          {t(awaitingFinal ? "seeStandingsSoFar" : "seeFinalStandings")}
        </Button>
        {/* `EX-PDF05`, decision 8: the set on paper, under the standings. */}
        <ProblemSetPdfDoor contest={contest} locale={locale} buttonClass={DOOR_BUTTON} />
        {hasPractice ? (
          <Button type="button" variant="outlineDark" onClick={onShowProblems} className={DOOR_BUTTON}>
            {t("practiceTheProblems")}
          </Button>
        ) : null}
      </div>
      <ContestCertificateDoor contest={contest} />
      {/* `EX-TAL01`: after the round a contestant may still give or take back the Talent Pass. */}
      {contest.viewer.isRegistered && talentPassMayShow(contest) ? (
        <div className="mt-5">
          <TalentPassCard slug={contest.slug} />
        </div>
      ) : null}
    </div>
  );
}

/** The door card on the head: a finished contest's, or the countdown and the registration door. */
function ContestDoor({
  contest,
  locale,
  now,
  onShowStandings,
  onShowProblems,
}: {
  contest: PublicContestDetail;
  locale: string;
  now: number | null;
  onShowStandings: () => void;
  onShowProblems: () => void;
}) {
  const hasEnded = contest.phase === "ended" || contest.phase === "unfrozen";
  return (
    <div className="rounded-[14px] border border-white/10 bg-neutral-900 p-5 sm:p-6">
      <WarmUpDoorNote contest={contest} locale={locale} />
      {hasEnded ? (
        <EndedDoor
          contest={contest}
          locale={locale}
          onShowStandings={onShowStandings}
          onShowProblems={onShowProblems}
        />
      ) : (
        <>
          <DoorCountdown contest={contest} now={now} locale={locale} />
          <DivisionDoorNote contest={contest} locale={locale} />
          <RegistrationDoor contest={contest} locale={locale} />
          {/* `EX-CPG02`: a reminder for the day, before the window opens. */}
          {contest.phase === "draft" || contest.phase === "scheduled" ? (
            <ContestCalendarLinks contest={contest} locale={locale} />
          ) : null}
        </>
      )}
    </div>
  );
}

function AnnouncementsPanel({ slug, locale }: { slug: string; locale: string }) {
  const t = useTranslations("contest.announcements");
  const zone = useContestDisplayZone();
  const query = useContestAnnouncements(slug);
  const items = query.data?.items ?? [];

  if (query.isPending) return <Skeleton className="h-32 w-full rounded-xl" />;

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-neutral-200 bg-white px-6 py-12 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Megaphone className="h-5 w-5" aria-hidden />
        </span>
        <p className="mt-1 text-base font-bold text-neutral-900">{t("empty")}</p>
        <p className="max-w-md text-sm leading-relaxed text-neutral-500">{t("emptyBody")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item._id} className="rounded-xl border border-neutral-200 bg-white p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-neutral-900">{item.title ?? t("untitled")}</p>
            <span className="text-xs text-neutral-500">{formatDateTime(item.postedAt, locale, zone)}</span>
          </div>
          <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">{item.body}</p>
        </div>
      ))}
    </div>
  );
}

function MySubmissionsStrip({
  slug,
  locale,
  limit,
  onSeeAll,
  virtual = false,
}: {
  slug: string;
  locale: string;
  /** The problems tab shows the last few; the submissions tab shows them all. */
  limit?: number;
  onSeeAll?: () => void;
  /** `EX-VRT01`: the reader's own rows are their virtual try's. */
  virtual?: boolean;
}) {
  const t = useTranslations("contest.page");
  const zone = useContestDisplayZone();
  const query = useMyContestSubmissions(slug, true, {}, virtual);
  const items = query.data?.items ?? [];

  if (items.length === 0) {
    return limit === undefined ? (
      <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
        {t("noSubmissions")}
      </p>
    ) : null;
  }

  const shown = limit === undefined ? items : items.slice(0, limit);

  return (
    <div className="rounded-xl border border-[rgb(var(--border))] bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
          {t("mySubmissions")}
        </p>
        {onSeeAll && items.length > shown.length ? (
          <Button
            variant="tertiary"
            type="button"
            onClick={onSeeAll}
            className="min-h-11 px-2 text-xs font-semibold text-primary hover:underline"
          >
            {t("seeAllSubmissions", { count: items.length })}
          </Button>
        ) : null}
      </div>
      <div className="mt-2 divide-y divide-[rgb(var(--border))]">
        {shown.map((item) => (
          <div key={item.submissionId} className="flex items-center gap-3 py-2">
            <span className="w-6 shrink-0 text-sm font-bold text-neutral-900">
              {item.letter ?? "-"}
            </span>
            <VerdictChip status={item.status} verdict={item.verdict ?? null} />
            <span className="ml-auto text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
              {formatDateTime(item.createdAt, locale, zone)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `/contests/[slug]` - the contest page, `docs/29` §5 and §6.
 *
 * Soft-authenticated: a stranger reads the window, the description and the
 * announcements, and the problem index appears only when the window opens
 * (`problems: null` before it, for everybody who is not a moderator - the count
 * of problems is itself withheld). Nothing on this page decides for itself who
 * may do what; every control renders off the payload's `viewer`.
 *
 * `EX-PUB22` drew it to the "Judge pages" proposal: the dark head says whose
 * contest it is and what state it is in, with one door per state beside it,
 * and the tabs stay under the Judge bar while the page scrolls.
 */
export function ContestPageClient({ slug, locale }: { slug: string; locale: string }) {
  const t = useTranslations("contest.page");
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";

  // `EX-CPG02`, Wave 30 decision 3: `null` until a reader picks a tab or the
  // address names one, and then the phase's own tab is open: the Overview
  // before the start and after the end, the Problems while the window is open.
  const [tab, setTab] = useState<Tab | null>(null);
  // `EX-SCB05`: the letter and list a standings popup's "Open in Submissions"
  // asked for; the Submissions tab opens on them.
  const [submissionsFocus, setSubmissionsFocus] = useState<{ letter: string; scope: "mine" | "field" } | null>(null);
  // `EX-SCB01`: `?kiosk=1` is the scoreboard for a projector. Read the same way
  // as `?tab=`, for the same reason: a value only the client uses must not put
  // a statically rendered page behind a Suspense boundary.
  const [kiosk, setKiosk] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const now = useNow();
  // `EX-SPN01`: the page's opens, for the organizer's sponsor sheet.
  useContestPageView(slug);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const requested = search.get("tab");
    if (isTab(requested)) setTab(requested);
    if (search.get("kiosk") === "1") setKiosk(true);
  }, []);

  // The URL follows the tab so a refresh, a bookmark and the back button all
  // land where the reader was. `replaceState` rather than a router push: the
  // page's data is unchanged and a navigation would refetch it mid-round.
  // `EX-CPG02`: always named, because a plain address now opens the phase's
  // own tab, which changes at the start.
  const selectTab = useCallback((value: Tab) => {
    setTab(value);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", value);
    window.history.replaceState(null, "", url.toString());
  }, []);

  // `EX-PUB22`: the door's buttons open a tab and bring it into view, because
  // on a phone the door is the last thing on the head and the tab is below it.
  const showSection = useCallback(
    (value: Tab) => {
      selectTab(value);
      document.getElementById("contest-sections")?.scrollIntoView?.({ block: "start" });
    },
    [selectTab],
  );
  const openSubmissions = useCallback(
    (target: { letter: string; scope: "mine" | "field" }) => {
      setSubmissionsFocus(target);
      showSection("submissions");
    },
    [showSection],
  );

  // `kiosk`, like `tab`, only updates from a client effect, so a `<Link>` that
  // only changes the query string never remounts `ContestPageClient` and the
  // mount-only read above never fires again. These two flip the state directly
  // (`selectTab`'s own fix for the same class of bug) so entering and leaving
  // the big screen take effect on the click that asked for them.
  const enterKiosk = useCallback(() => {
    setKiosk(true);
    const url = new URL(window.location.href);
    url.searchParams.set("kiosk", "1");
    window.history.replaceState(null, "", url.toString());
  }, []);

  const exitKiosk = useCallback(() => {
    setKiosk(false);
    setTab("standings");
    const url = new URL(window.location.href);
    url.searchParams.delete("kiosk");
    url.searchParams.set("tab", "standings");
    window.history.replaceState(null, "", url.toString());
  }, []);

  const query = usePublicContest(slug, viewerKey, !authLoading);
  // `EX-PUB22`: the Announcements tab counts what it holds. The panel reads the
  // same key, so opening it costs nothing more. `EX-ANN01`: the same read is
  // the inbox, so a new announcement is shown on every other tab too.
  const announcementInbox = useAnnouncementInbox(slug, query.data, !authLoading);
  const { markSeen: markAnnouncementsSeen } = announcementInbox;
  const freshAnnouncements = announcementInbox.unseen.length;
  // `EX-ANN02`: an answered clarification too, for a competing viewer.
  const clarificationInbox = useClarificationInbox(slug, query.data, !authLoading);
  const { markSeen: markClarificationsSeen } = clarificationInbox;
  const freshClarifications = clarificationInbox.unseen.length;
  useTitleCount(freshAnnouncements + freshClarifications);
  // Reading the tab is reading them.
  useEffect(() => {
    if (tab === "announcements" && freshAnnouncements > 0) markAnnouncementsSeen();
    if (tab === "clarifications" && freshClarifications > 0) markClarificationsSeen();
  }, [tab, freshAnnouncements, markAnnouncementsSeen, freshClarifications, markClarificationsSeen]);
  const contest = query.data;
  const zone = useContestDisplayZone();

  // `EX-CPG02`: the compact bar shows once the head's tabs have scrolled under
  // the Judge bar (64 px), and hides again when they come back.
  const headTabsRef = useRef<HTMLDivElement | null>(null);
  const [headTabsInView, setHeadTabsInView] = useState(true);
  const contestLoaded = Boolean(contest);
  useEffect(() => {
    const node = headTabsRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setHeadTabsInView(entry ? entry.isIntersecting : true), {
      rootMargin: "-64px 0px 0px 0px",
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [contestLoaded, kiosk]);

  if (query.isPending || authLoading) {
    return (
      <>
        <div className="bg-neutral-950 pb-11 pt-7">
          <Container>
            <Skeleton className="h-4 w-28 bg-white/10" />
            <Skeleton className="mt-5 h-7 w-72 max-w-full bg-white/10" />
            <Skeleton className="mt-4 h-11 w-[520px] max-w-full bg-white/10" />
            <Skeleton className="mt-5 h-5 w-[640px] max-w-full bg-white/10" />
          </Container>
        </div>
        <Container className="pt-7">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="mt-4 h-64 w-full rounded-xl" />
        </Container>
      </>
    );
  }

  if (query.isError || !contest) {
    const isMissing = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div className="py-16">
        <Container>
          <div className="mx-auto max-w-md rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center">
            <p className="text-base font-bold text-neutral-900">
              {isMissing ? t("notFoundTitle") : t("loadFailedTitle")}
            </p>
            <p className="mt-2 text-sm text-[rgb(var(--muted-foreground))]">
              {isMissing ? t("notFoundBody") : t("loadFailedBody")}
            </p>
            <Link
              href={`/${locale}/contests`}
              className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-primary/90"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("backToDirectory")}
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const letters = (contest.problems ?? []).map((problem) => problem.letter);
  // `EX-SCB03`: the standings' letters link to their problems, named by title.
  const problemTitles: Record<string, string> = {};
  for (const problem of contest.problems ?? []) {
    const title = locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en;
    if (title) problemTitles[problem.letter] = title;
  }

  const tabs: Tab[] = ["overview", "problems", "standings"];
  // `EX-VRT01`: somebody with a virtual try at this round; their own rows are its rows.
  const virtualTry = Boolean(virtualOf(contest)?.attempt);
  /**
   * `EX-PUB14`: own submissions, always own (`docs/29` §10). Registered, because
   * a submission list is otherwise a page with nothing on it.
   *
   * `EX-PUB16` added the second reason it can be worth opening: once the round
   * is over the field's list is public, and a reader who never competed is
   * exactly the person who came to see how it went.
   */
  if (contest.viewer.isRegistered || contest.submissionsRevealed === true || virtualTry) {
    tabs.push("submissions");
  }
  // `EX-TST01`: the round's staff (and its moderators) read the rehearsals;
  // for everybody else the tab does not exist.
  const readsRehearsals = canReadRehearsals(contest, isAuthenticated);
  const rehearsing = canRehearseNow(contest, isAuthenticated);
  if (readsRehearsals) tabs.push("rehearsal");
  tabs.push("announcements");
  // Reading a clarification needs an active participant (`docs/29` §6.2), so the
  // tab only exists for somebody the server will answer.
  if (contest.viewer.isCompeting || contest.viewer.canModerate) tabs.push("clarifications");
  // `EX-INS01`, `docs/29` §6.25: the institutions open at the end, where the
  // stamp carries them and the board shows the institution; the season where
  // a published season holds this round.
  const roundEnded = contest.phase === "ended" || contest.phase === "unfrozen";
  if (contest.institutionTable === true && roundEnded) tabs.push("institutions");
  const seasons = contest.seasons ?? [];
  if (seasons.length > 0) tabs.push("season");
  // A `?tab=` that this reader may not open falls back rather than showing a
  // blank panel: the link is shared, the rights are the reader's. With none,
  // the phase's own tab (`EX-CPG02`).
  const defaultTab: Tab = contest.isOpen ? "problems" : "overview";
  const activeTab: Tab = tab !== null && tabs.includes(tab) ? tab : defaultTab;

  const counts: Partial<Record<Tab, number>> = {
    ...(contest.problems ? { problems: contest.problems.length } : {}),
    ...(announcementInbox.total !== undefined ? { announcements: announcementInbox.total } : {}),
  };

  if (kiosk) {
    return <ContestKioskView contest={contest} locale={locale} onExit={exitKiosk} />;
  }

  // `EX-CPG02`, Wave 30 decision 5: the sections as links on the head's bottom
  // edge, each its own address; a click switches in place (`selectTab`).
  const contestPath = `/${locale}/contests/${contest.slug}`;
  const hasEnded = contest.phase === "ended" || contest.phase === "unfrozen";
  const headTabItems: Array<HeadTabItem<Tab | "rules">> = [
    ...tabs.map((value): HeadTabItem<Tab | "rules"> => {
      const count = counts[value];
      const fresh =
        (value === "announcements" && freshAnnouncements > 0) || (value === "clarifications" && freshClarifications > 0);
      return {
        key: value,
        label: t(`tab_${value}`),
        href: `${contestPath}?tab=${value}`,
        icon: TAB_ICON[value],
        ...(count !== undefined ? { count: formatNumber(count, locale) } : {}),
        // Before the start the Problems tab says when it opens, never how many (`docs/29` §6.1).
        ...(value === "problems" && contest.problems === null && contest.startAt && !hasEnded
          ? {
              hint: (
                <>
                  <Lock className="h-[11px] w-[11px]" aria-hidden />
                  {formatClockTime(contest.startAt, locale, zone)}
                </>
              ),
            }
          : {}),
        ...(fresh ? { dot: true, dotLabel: t("tabNew") } : {}),
      };
    }),
    {
      key: "rules",
      label: t("tab_rules"),
      href: `${contestPath}/rules`,
      icon: <ScrollText className="h-4 w-4" />,
      follow: true,
    },
  ];
  const pickTab = (value: Tab | "rules") => {
    if (value !== "rules") selectTab(value);
  };
  // The bar's Register brings the door back: the door knows every state the
  // button would otherwise have to repeat.
  const barRegister =
    !hasEnded &&
    !contest.viewer.isRegistered &&
    contest.viewer.staffRole === null &&
    (contest.viewer.canRegister || (!contest.viewer.isAuthenticated && contest.registration === "open"))
      ? () => window.scrollTo({ top: 0, behavior: smoothScrollBehavior() })
      : null;

  return (
    <>
      <ContestHead
        contest={contest}
        locale={locale}
        door={
          <ContestDoor
            contest={contest}
            locale={locale}
            now={now}
            onShowStandings={() => showSection("standings")}
            onShowProblems={() => showSection("problems")}
          />
        }
        tabs={
          <div ref={headTabsRef}>
            <HeadTabs items={headTabItems} active={activeTab} label={t("sectionsLabel")} onSelect={pickTab} />
          </div>
        }
      />

      {/* `EX-CPG02`: the same tabs on a compact bar once the head has scrolled away. */}
      <ContestStickyBar
        contest={contest}
        locale={locale}
        visible={!headTabsInView}
        now={now}
        onRegister={barRegister}
        tabs={
          <HeadTabs
            items={headTabItems}
            active={activeTab}
            label={t("sectionsLabel")}
            onSelect={pickTab}
            variant="compact"
            className="w-fit max-w-full"
          />
        }
      />

      <Container className="pb-16 pt-7 sm:pb-[72px]">
        <div id="contest-sections" className="scroll-mt-[136px] space-y-4">
          {/* `EX-VRT01`: the try's clock over every tab while it runs. */}
          <VirtualBar contest={contest} locale={locale} />
          {activeTab !== "announcements" ? (
            <ContestAnnouncementAlert
              unseen={announcementInbox.unseen}
              onSeen={markAnnouncementsSeen}
              onReadAll={() => selectTab("announcements")}
            />
          ) : null}
          {activeTab !== "clarifications" ? (
            <ContestClarificationAlert
              unseen={clarificationInbox.unseen}
              onSeen={markClarificationsSeen}
              onReadAll={() => selectTab("clarifications")}
            />
          ) : null}
          {/* `EX-CPG02`, Wave 30 decision 3: what a reader wants before the start, and after the end. */}
          {activeTab === "overview" ? (
            <>
              {/* `EX-VRT01`: a finished virtual try, apart from every real result. */}
              <VirtualResultCard
                contest={contest}
                locale={locale}
                onShowBoard={() => selectTab("standings")}
                onShowProblems={() => selectTab("problems")}
              />
              <ContestOverview
                contest={contest}
                locale={locale}
                onShowProblems={() => showSection("problems")}
                onOpenGuide={() => setGuideOpen(true)}
              />
              <ContestFirstTimerFaq
                slug={contest.slug}
                locale={locale}
                official={contest.official}
                rated={contest.rated}
                hasFreeze={contest.freezeAt !== null}
                onOpenGuide={contest.phase === "draft" || contest.phase === "scheduled" ? () => setGuideOpen(true) : undefined}
              />
            </>
          ) : null}

          {activeTab === "problems" ? (
            contest.problems === null ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-neutral-200 bg-white px-6 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Lock className="h-5 w-5" aria-hidden />
                </span>
                <p className="mt-1 text-base font-bold text-neutral-900">{t("problemsLockedTitle")}</p>
                <p className="max-w-md text-sm leading-relaxed text-neutral-500">{t("problemsLockedBody")}</p>
                {/*
                  `EX-EDR05`: the wait before the start is when there is time to
                  learn the editor, so the guide is here whatever the owner's
                  switch says (Wave 23 decision 7). After the end it is gone.
                */}
                {contest.phase === "draft" || contest.phase === "scheduled" ? (
                  <Button type="button" variant="outline" className="mt-3" onClick={() => setGuideOpen(true)}>
                    <CircleHelp className="h-4 w-4 text-primary" aria-hidden />
                    {t("howSubmitting")}
                  </Button>
                ) : null}
              </div>
            ) : (
              <>
                {rehearsing ? (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm leading-relaxed text-neutral-800">
                    {t("rehearsalBanner", { role: t(`staffRole_${contest.viewer.staffRole ?? "tester"}`) })}{" "}
                    <button
                      type="button"
                      onClick={() => selectTab("rehearsal")}
                      className="font-semibold text-primary underline underline-offset-2"
                    >
                      {t("rehearsalBannerLink")}
                    </button>
                  </div>
                ) : null}
                {/*
                  `EX-PDF05`: the set on paper. Staff (decision 7) before the
                  end, by the rehearsal line; anyone signed in after a public
                  round ends (decision 8). Each decides for itself and draws
                  nothing otherwise, and never both at once.
                */}
                <StaffProblemSetPdf contest={contest} locale={locale} />
                <ProblemSetPdfBar contest={contest} locale={locale} />
                <ArchiveNotice problems={contest.problems} roundArchived={contest.archivedAt !== null} />
                {/*
                  `EX-PUB15`: the list, with the field's numbers and the reader's
                  own state on it. Both come from queries this page already makes.
                */}
                <ContestProblemList contest={contest} locale={locale} />
                {contest.viewer.isRegistered || virtualTry ? (
                  <MySubmissionsStrip
                    slug={contest.slug}
                    locale={locale}
                    limit={10}
                    onSeeAll={() => selectTab("submissions")}
                    virtual={virtualTry}
                  />
                ) : null}
              </>
            )
          ) : null}

          {activeTab === "standings" ? (
            // `EX-VRT01`: the real board at the try's minute while it runs; after
            // it, where the reader stood beside the round's final board.
            <VirtualBoardSwitch
              contest={contest}
              ghost={
                <ContestStandingsPanel
                  slug={contest.slug}
                  locale={locale}
                  canModerate={false}
                  problemTitles={problemTitles}
                  stickyTop={121}
                  virtual
                />
              }
              final={
                <ContestStandingsPanel
                  slug={contest.slug}
                  locale={locale}
                  canModerate={contest.viewer.canModerate}
                  onOpenBigScreen={enterKiosk}
                  problemTitles={problemTitles}
                  // The Judge bar (`h-16`) and the tabs under it (`h-14` and a border) stay on screen.
                  stickyTop={121}
                  // `EX-SPN01`: the sponsors in the standings head, when the organizer prints them there.
                  sponsors={contest.brand?.show.standings ? contest.brand.sponsors : undefined}
                  // `EX-SCB05`: a result opens its code, by the server's own rules.
                  results={{
                    contestId: contest._id,
                    signedIn: contest.viewer.isAuthenticated,
                    sourceOpen: contest.sourceOpen === true,
                    onOpenSubmissions: openSubmissions,
                  }}
                />
              }
            />
          ) : null}

          {activeTab === "submissions" ? (
            <ContestSubmissionsPanel
              key={submissionsFocus ? `${submissionsFocus.scope}:${submissionsFocus.letter}` : "all"}
              contest={contest}
              locale={locale}
              initialFilter={submissionsFocus ?? undefined}
            />
          ) : null}

          {activeTab === "rehearsal" ? (
            <ContestRehearsalPanel slug={contest.slug} locale={locale} />
          ) : null}

          {activeTab === "announcements" ? (
            <AnnouncementsPanel slug={contest.slug} locale={locale} />
          ) : null}

          {activeTab === "institutions" ? <InstitutionsPanel slug={contest.slug} locale={locale} /> : null}

          {activeTab === "season" ? <SeasonPanel seasons={seasons} locale={locale} contestId={contest._id} /> : null}

          {activeTab === "clarifications" ? (
            <ContestClarificationsPanel
              slug={contest.slug}
              locale={locale}
              canAsk={contest.viewer.isCompeting && contest.isOpen}
              problemLetters={letters}
            />
          ) : null}
        </div>
      </Container>

      {guideOpen ? (
        <ContestSubmitGuide
          locale={locale}
          onClose={() => setGuideOpen(false)}
          from="page"
          // The clipboard rule reaches the page only inside the window
          // (`viewer.integrity`); before the start it is unknown and the
          // guide keeps all eight steps.
          {...(contest.viewer.integrity ? { uploadOff: contest.viewer.integrity.clipboard === "block" } : {})}
        />
      ) : null}
    </>
  );
}
